import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  DestroyRef,
  inject,
  HostListener,
  Inject,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroupDirective } from '@angular/forms';
import { Subject } from 'rxjs';
import { AnalyticsService } from '../analytics.service';
import { debounceTime } from 'rxjs/operators';
import { TrackBase } from '../track-base/track-base.directive';
import { DOCUMENT } from '@angular/common';

type InteractionType = 'focus' | 'blur';

@Directive({
  selector: '[appTrackForm]',
  standalone: false,
})
export class TrackFormDirective extends TrackBase implements OnInit, OnDestroy {
  @Input() screenName!: string;
  @Input() debounce = 2000;

  private destroyRef = inject(DestroyRef);

  /** Per-field + per-event debounce buckets */
  private interactionStreams = new Map<string, Subject<void>>();

  constructor(
    private el: ElementRef<HTMLFormElement>,
    private formGroupDir: FormGroupDirective,
    analytics: AnalyticsService,
    @Inject(DOCUMENT) document: Document,
  ) {
    super(analytics, document);
  }

  ngOnInit() {
    const form = this.formGroupDir.form;
    this.resolvedScreenName = this.resolveScreenName(this.screenName);

    /* -----------------------------
     * VALUE CHANGE TRACKING
     * ----------------------------- */
    form.valueChanges
      .pipe(debounceTime(this.debounce), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        Object.entries(form.controls).forEach(([field, control]) => {
          if (control.dirty) {
            this.analytics.trackEvent('input_interaction', {
              field_name: field,
              screen_name: this.resolvedScreenName,
              interaction_type: 'change',
            });
          }
        });
      });
  }

  /* -----------------------------
   * EVENT LISTENERS
   * ----------------------------- */

  @HostListener('focusin', ['$event'])
  onFocus(event: FocusEvent) {
    this.handleInteraction(event, 'focus');
  }

  @HostListener('focusout', ['$event'])
  onBlur(event: FocusEvent) {
    this.handleInteraction(event, 'blur');
  }

  /* -----------------------------
   * CORE LOGIC
   * ----------------------------- */

  private handleInteraction(event: FocusEvent, type: InteractionType): void {
    const fieldName = this.getFieldName(event.target as HTMLElement);
    if (!fieldName) return;

    const key = `${fieldName}:${type}`;

    if (!this.interactionStreams.has(key)) {
      const stream$ = new Subject<void>();

      stream$
        .pipe(debounceTime(this.debounce), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.analytics.trackEvent('input_interaction', {
            field_name: fieldName,
            screen_name: this.resolvedScreenName,
            interaction_type: type,
          });
        });

      this.interactionStreams.set(key, stream$);
    }

    // Cancels previous pending event for the same field + interaction
    this.interactionStreams.get(key)!.next();
  }

  /* -----------------------------
   * FIELD NAME RESOLUTION
   * ----------------------------- */

  private getFieldName(target: HTMLElement): string | null {
    let element: HTMLElement | null = target;

    while (element && element !== this.el.nativeElement) {
      const controlName =
        element.getAttribute?.('fieldName') ||
        element.getAttribute?.('fieldname') ||
        element.getAttribute?.('formControlName') ||
        element.getAttribute?.('formcontrolname');

      if (controlName) {
        return controlName;
      }

      element = element.parentElement;
    }

    return target.getAttribute?.('name') || null;
  }

  ngOnDestroy() {
    this.interactionStreams.forEach((s) => s.complete());
    this.interactionStreams.clear();
  }
}
